import React, { useState } from 'react';
import { apiService } from '../../../../services/api';
import MaskedInput from '../../contacts/components/MaskedInput';
import LocationSelector from '../../contacts/components/LocationSelector';
import { CreateContatoData } from '../../../../types/contato';
import { toast } from 'react-toastify';
import './QuickContactForm.css';

interface QuickContactFormProps {
  onSuccess: (newContact: any) => void;
  onCancel: () => void;
}

const QuickContactForm: React.FC<QuickContactFormProps> = ({
  onSuccess,
  onCancel
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newContato, setNewContato] = useState<CreateContatoData>({
    nome: '',
    tipo_entidade: 'PF',
    email: '',
    telefone: '',
    id_municipio: undefined,
    estado: ''
  });

  const handleInputChange = (field: keyof CreateContatoData, value: any) => {
    setNewContato(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Validações básicas ANTES de setar isSubmitting
    if (newContato.tipo_entidade === 'PF') {
      if (!newContato.nome?.trim()) {
        toast.error('O nome é obrigatório');
        return;
      }
    } else {
      if (!newContato.razao_social?.trim()) {
        toast.error('A razão social é obrigatória');
        return;
      }
    }

    if (!newContato.email?.trim()) {
      toast.error('O email é obrigatório');
      return;
    }

    if (!newContato.telefone) {
      toast.error('O telefone é obrigatório');
      return;
    }

    try {
      setIsSubmitting(true);

      // Preparar dados para envio
      const cleanData = {
        ...newContato,
        telefone: newContato.telefone?.replace(/\D/g, ''),
        whatsapp: newContato.whatsapp?.replace(/\D/g, ''),
        cnpj: newContato.cnpj?.replace(/\D/g, ''),
        cpf: newContato.cpf?.replace(/\D/g, ''),
        cep: newContato.cep?.replace(/\D/g, '')
      };

      const contatoData: CreateContatoData = {
        tipo_entidade: cleanData.tipo_entidade,
        nome: cleanData.tipo_entidade === 'PF' ? cleanData.nome?.trim() : cleanData.razao_social?.trim() || '',
        email: cleanData.email.trim(),
        telefone: cleanData.telefone,
        id_municipio: cleanData.id_municipio,
        estado: cleanData.estado,
        ...(cleanData.tipo_entidade === 'PF'
          ? { cpf: cleanData.cpf }
          : { razao_social: cleanData.razao_social, cnpj: cleanData.cnpj }
        )
      };

      const response = await apiService.createContato(contatoData);

      if (response && response.data) {
        // Limpar form antes de chamar onSuccess
        setNewContato({
          nome: '',
          tipo_entidade: 'PF',
          email: '',
          telefone: '',
          id_municipio: undefined,
          estado: ''
        });

        // Chamar callback de sucesso (o toast será mostrado no parent)
        onSuccess(response.data);
      }
    } catch (error) {
      console.error('Erro ao criar contato:', error);
      toast.error('Erro ao criar cliente');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="quick-contact-form-wrapper">
      <div className="alert alert-info d-flex align-items-center mb-3">
        <i className="ti ti-info-circle me-2"></i>
        <span>Cadastro rápido - Preencha os dados essenciais do cliente</span>
      </div>

      <div className="row">
          {/* Tipo de Pessoa */}
          <div className="col-12 mb-3">
            <label className="form-label mb-2">Tipo de Pessoa <span className="text-danger">*</span></label>
            <div className="d-flex gap-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="tipoEntidadeQuick"
                  id="quickRadioPF"
                  checked={newContato.tipo_entidade === 'PF'}
                  onChange={() => handleInputChange('tipo_entidade', 'PF')}
                />
                <label className="form-check-label" htmlFor="quickRadioPF">
                  Pessoa Física
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="tipoEntidadeQuick"
                  id="quickRadioPJ"
                  checked={newContato.tipo_entidade === 'PJ'}
                  onChange={() => handleInputChange('tipo_entidade', 'PJ')}
                />
                <label className="form-check-label" htmlFor="quickRadioPJ">
                  Pessoa Jurídica
                </label>
              </div>
            </div>
          </div>

          {/* Nome ou Razão Social */}
          {newContato.tipo_entidade === 'PF' ? (
            <div className="col-md-6 mb-3">
              <label className="form-label">Nome <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={newContato.nome || ''}
                onChange={(e) => handleInputChange('nome', e.target.value)}
                placeholder="Digite o nome completo"
                autoFocus
              />
            </div>
          ) : (
            <div className="col-md-6 mb-3">
              <label className="form-label">Razão Social <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={newContato.razao_social || ''}
                onChange={(e) => handleInputChange('razao_social', e.target.value)}
                placeholder="Digite a razão social"
                autoFocus
              />
            </div>
          )}

          {/* CPF ou CNPJ */}
          {newContato.tipo_entidade === 'PF' ? (
            <div className="col-md-6 mb-3">
              <label className="form-label">CPF</label>
              <MaskedInput
                value={newContato.cpf || ''}
                onChange={(value) => handleInputChange('cpf', value)}
                mask="cpf"
                placeholder="000.000.000-00"
              />
            </div>
          ) : (
            <div className="col-md-6 mb-3">
              <label className="form-label">CNPJ</label>
              <MaskedInput
                value={newContato.cnpj || ''}
                onChange={(value) => handleInputChange('cnpj', value)}
                mask="cnpj"
                placeholder="00.000.000/0000-00"
              />
            </div>
          )}

          {/* Email */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Email <span className="text-danger">*</span></label>
            <input
              type="email"
              className="form-control"
              value={newContato.email || ''}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="email@exemplo.com"
            />
          </div>

          {/* Telefone */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Telefone <span className="text-danger">*</span></label>
            <MaskedInput
              value={newContato.telefone || ''}
              onChange={(value) => handleInputChange('telefone', value)}
              mask="phone"
              placeholder="(00) 00000-0000"
            />
          </div>

          {/* Localização */}
          <div className="col-12 mb-3">
            <label className="form-label mb-0">Localização</label>
            <div className="text-muted small mb-2">
              Importante para preencher automaticamente o município na oportunidade
            </div>
            <LocationSelector
              selectedState={newContato.estado}
              selectedMunicipality={newContato.id_municipio ? Number(newContato.id_municipio) : undefined}
              onStateChange={(estado) => {
                setNewContato(prev => ({
                  ...prev,
                  estado: estado,
                  id_municipio: undefined
                }));
              }}
              onMunicipalityChange={(municipioId) => {
                setNewContato(prev => ({
                  ...prev,
                  id_municipio: Number(municipioId)
                }));
              }}
              apiService={apiService}
            />
          </div>

          {/* Botões de Ação */}
          <div className="col-12">
            <div className="d-flex gap-2 justify-content-end pt-2 border-top">
              <button
                type="button"
                className="btn btn-light"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                <i className="ti ti-x me-1"></i>
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={(e) => handleSubmit(e as any)}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="ti ti-check me-1"></i>
                    Salvar Cliente
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
  );
};

export default QuickContactForm;
